import { forwardRef, type ReactNode, type MouseEvent } from 'react';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

/**
 * RwTag — the DS Tag (rw-ds.css `.rw-tag`).
 *
 * Two jobs: a **category / type label** (accent colour variants) and an **interactive
 * filter chip** (`selectable`, reverse-out when `selected`). For status hues use
 * `RwLozenge`. Anatomy: Storybook → Components → Tag; governance → rw-ds-components § Tag.
 *
 * A `selectable` tag renders a real `<button>` (keyboard-focusable, `aria-pressed`);
 * the colour variant is dropped because the selectable styling owns fill/border.
 * `sx` is layout-only.
 *
 * SELECTABLE + DISMISSABLE IS A SUPPORTED COMBINATION, not a conflict — rw-ds.css
 * styles `.rw-tag--selectable.rw-tag--selected .rw-tag__dismiss` (plus its hover) in
 * full-strength `--icon-neutral-on-color`, and its §MULTISELECT TRIGGER note says
 * "Uses existing .rw-tag--selectable.rw-tag--selected.rw-tag--dismissable". Filter
 * tags pass `dismissable` only while `selected`: the ✕ removes the applied value, so
 * on an unselected tag there is nothing for it to do. The ✕ `stopPropagation`s, so it
 * never also fires the tag's own toggle.
 */
export type RwTagVariant = 'neutral' | 'teal' | 'orange' | 'magenta' | 'lime' | 'purple';

export interface RwTagProps {
  variant?: RwTagVariant;
  size?: 'sm' | 'md';
  /** Interactive filter chip — renders a <button>; toggle state via `selected`. */
  selectable?: boolean;
  selected?: boolean;
  /** Trailing dismiss ✕ (fires `onDismiss`). */
  dismissable?: boolean;
  onDismiss?: () => void;
  /** Accessible label for the dismiss ✕ (default "Remove"); pass a contextual value. */
  dismissLabel?: string;
  onClick?: (e: MouseEvent) => void;
  /** Leading Material icon node (16px, sized by CSS). */
  icon?: ReactNode;
  className?: string;
  sx?: SxProps<Theme>;
  children?: ReactNode;
  'aria-label'?: string;
}

const RwTag = forwardRef<HTMLElement, RwTagProps>(function RwTag(
  {
    variant = 'neutral',
    size = 'md',
    selectable = false,
    selected = false,
    dismissable = false,
    onDismiss,
    dismissLabel = 'Remove',
    onClick,
    icon,
    className,
    sx,
    children,
    ...rest
  },
  ref
) {
  const classes = [
    'rw-tag',
    // Selectable styling owns fill/border; a colour variant only applies to static tags.
    selectable ? '' : `rw-tag--${variant}`,
    size === 'sm' ? 'rw-tag--sm' : '',
    selectable ? 'rw-tag--selectable' : '',
    selectable && selected ? 'rw-tag--selected' : '',
    dismissable ? 'rw-tag--dismissable' : '',
    className ?? ''
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Box
      component={selectable ? 'button' : 'span'}
      ref={ref}
      className={classes}
      onClick={onClick}
      type={selectable ? 'button' : undefined}
      aria-pressed={selectable ? selected : undefined}
      sx={{ '& .MuiSvgIcon-root': { fontSize: 'var(--size-16)' }, ...sx }}
      {...rest}
    >
      {icon}
      {children}
      {dismissable && (
        <Box
          component="button"
          type="button"
          className="rw-tag__dismiss"
          aria-label={dismissLabel}
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            onDismiss?.();
          }}
        >
          <CloseIcon />
        </Box>
      )}
    </Box>
  );
});

export default RwTag;
// Named alias — see RwButton.tsx for why this stays alongside the default export.
export { RwTag };
